import { put, select, call } from 'redux-saga/effects';
import { ajaxLoader, updateGlobalError } from '../config/config.actions';
import { Leave, LeaveRecsQuery } from './leaves.model';
import {
  leavePageLoaded,
  leavePageToggleLoading,
  leavesRequested,
  leaveDetailLoaded,
  leaveApplySuccess,
  leaveUpdateSuccess,
  leaveDeleteSuccess,
  leaveTypesLoaded,
  leaveActionToggleLoading,
} from './leaves.actions';
import {
  getLeaveRecords,
  getLeaveDetail,
  applyLeave,
  updateLeave,
  deleteLeave,
  getLeaveTypes,
} from './leaves.services';
import { ActionTypes, LeaveState } from './leaves.types';
import { constructErrorMessage } from '@biznessforce/ui-lib';

export function* leavesPageRequestedSaga(action: ActionTypes) {
  const {
    payload: { query },
  } = action as { payload: { query: LeaveRecsQuery } };

  const { lastQuery }: { lastQuery: LeaveRecsQuery } = yield select(
    (state: { leaves: LeaveState }) => state.leaves
  );

  yield put(ajaxLoader({ isLoading: true }));
  yield put(leavePageToggleLoading(true));

  // If query has value add property pageNumber to 1 else {}
  const dummyQuery = Object.is(query, {}) ? {} : { ...query, pageNumber: query?.pageNumber || 1 };

  const finalQuery = { ...lastQuery, ...dummyQuery };

  try {
    const { data, headers } = yield getLeaveRecords(finalQuery);
    if (data.length === 0 && parseInt(headers['x-total-count']) > 0 && finalQuery.pageNumber! > 1) {
      yield put(leavesRequested({ pageNumber: finalQuery.pageNumber! - 1 }));
    } else {
      yield put(
        leavePageLoaded({
          query: finalQuery,
          leaveRecords: data,
          totalCount: parseInt(headers['x-total-count']),
        })
      );
    }
    yield put(updateGlobalError(undefined));
  } catch (error) {
    yield put(updateGlobalError({ type: 'error', msg: constructErrorMessage(error) }));
  }
  yield put(ajaxLoader({ isLoading: false }));
}

// New saga functions
export function* leaveDetailRequestedSaga(action: ActionTypes) {
  const {
    payload: { leaveId, callback },
  } = action as { payload: { leaveId: string; callback?: () => void } };

  yield put(ajaxLoader({ isLoading: true }));
  yield put(leaveActionToggleLoading(true));

  try {
    const { data } = yield call(getLeaveDetail, leaveId);
    yield put(leaveDetailLoaded(data));
    yield put(updateGlobalError(undefined));
    if (callback) callback();
  } catch (error) {
    yield put(updateGlobalError({ type: 'error', msg: constructErrorMessage(error) }));
  }
  yield put(ajaxLoader({ isLoading: false }));
  yield put(leaveActionToggleLoading(false));
}

export function* leaveApplyRequestedSaga(action: ActionTypes) {
  const {
    payload: { leaveData, callback },
  } = action as {
    payload: { leaveData: Leave; callback?: (responseLeave: Leave) => void };
  };

  // yield put(ajaxLoader({ isLoading: true }));
  yield put(leaveActionToggleLoading(true));

  try {
    const { data: responseLeave } = yield call(applyLeave, leaveData);
    yield put(leaveApplySuccess());
    if (callback) callback(responseLeave);
    yield put(updateGlobalError(undefined));
    // yield put(leavesRequested({ pageNumber: 1, pageSize: 10 })); // Refresh the leave list
  } catch (error) {
    yield put(updateGlobalError({ type: 'error', msg: constructErrorMessage(error) }));
  }
  // yield put(ajaxLoader({ isLoading: false }));
  yield put(leaveActionToggleLoading(false));
}

export function* leaveUpdateRequestedSaga(action: ActionTypes) {
  const {
    payload: { leaveData, callback },
  } = action as { payload: { leaveData: Leave; callback?: () => void } };

  yield put(ajaxLoader({ isLoading: true }));
  yield put(leaveActionToggleLoading(true));

  try {
    yield call(updateLeave, leaveData);
    yield put(leaveUpdateSuccess());
    if (callback) callback();
    yield put(updateGlobalError(undefined));
    yield put(leavesRequested({})); // Refresh the leave list
  } catch (error) {
    yield put(updateGlobalError({ type: 'error', msg: constructErrorMessage(error) }));
  }
  yield put(ajaxLoader({ isLoading: false }));
  yield put(leaveActionToggleLoading(false));
}

export function* leaveDeleteRequestedSaga(action: ActionTypes) {
  const {
    payload: { leaveId, callback },
  } = action as {
    payload: { leaveId: string; callback?: () => void };
  };

  yield put(ajaxLoader({ isLoading: true }));
  yield put(leaveActionToggleLoading(true));

  try {
    yield call(deleteLeave, leaveId);
    yield put(leaveDeleteSuccess());
    if (callback) callback();
    yield put(updateGlobalError(undefined));
    yield put(
      leavesRequested({
        pageNumber: 1,
        pageSize: 10,
      })
    ); // Refresh the leave list
  } catch (error) {
    yield put(updateGlobalError({ type: 'error', msg: constructErrorMessage(error) }));
  }
  yield put(ajaxLoader({ isLoading: false }));
  yield put(leaveActionToggleLoading(false));
}

export function* leaveTypesRequestedSaga() {
  // yield put(ajaxLoader({ isLoading: true }));

  try {
    const { data } = yield call(getLeaveTypes);

    const leaveTypes = data.map((lt) => ({
      label: `(${lt.code}) - ${lt.name}`,
      value: lt.lvTypeUuid,
    }));

    yield put(leaveTypesLoaded(leaveTypes));
    // yield put(updateGlobalError(undefined));
  } catch (error) {
    yield put(updateGlobalError({ type: 'error', msg: constructErrorMessage(error) }));
  }
}
